<template>
  <div class="page-container billing-guides-page gd-scope">
    <portal to="page-name">Guias</portal>
    <div class="card card-page" v-if="hasAccess">
      <div class="card-header">
        <button class="btn btn-primary btn-icon btn-icon-left float-right ml-2"
                :disabled="!canUpdate" @click="newGuide.show = true">
          <fa-icon :icon="['fal', 'plus']"/>Nova guia
        </button>
        <h1 class="card-title">Guias</h1>
      </div>
      <div class="card-body">
        <guides-summary
          :summary="summary"
          :loading="summaryLoading"
          :period-text="periodText"
          :plan-name="planName"
          :active="summaryActive"
          @filter="applySummaryFilter"
        />

        <div class="cr-panel">
          <div class="cr-panel-head">
            <div class="cr-panel-title">
              <fa-icon :icon="['fal', 'file-medical-alt']" class="cr-panel-icon"/>
              Guias
              <span class="label label-rounded" v-if="!loading">{{ data.total }}</span>
            </div>
            <div class="cr-panel-subtitle">{{ viewText }}</div>
          </div>

          <guides-toolbar
            ref="toolbar"
            :filter="filter"
            :v="$v.filter"
            :insurances="insurances"
            :professionals="professionals"
            :loading="loading"
            @search="reload"
            @clear="clearFilters"
            @change-plan="planChanged"
            @change-link="setLink"
          />

          <div class="gd-situation" v-if="situationLabel">
            <span class="chip">
              <fa-icon :icon="['fal', 'filter']"/>{{ situationLabel }}
              <button class="btn btn-clear tooltip tooltip-left" data-tooltip="Remover filtro"
                      @click="clearSituation"></button>
            </span>
          </div>

          <div class="loading loading-lg cr-panel-loading" v-if="loading"></div>
          <template v-else>
            <div class="empty cr-panel-empty" v-if="error">
              <div class="empty-icon">
                <fa-icon :icon="['fal', 'exclamation-triangle']" size="2x"/>
              </div>
              <p class="empty-title">Não foi possível carregar as guias</p>
              <p class="empty-subtitle">{{ error }}</p>
              <button class="btn btn-gray" @click="reload">Tentar de novo</button>
            </div>
            <div class="empty cr-panel-empty" v-else-if="!filter.insurancePlanId">
              <div class="empty-icon"><fa-icon :icon="['fal', 'shield-check']" size="2x"/></div>
              <p class="empty-title">Escolha o convênio para listar as guias</p>
              <p class="empty-subtitle">
                Selecione um convênio ou "Todos os convênios" e clique em Pesquisar.
              </p>
            </div>
            <div class="empty cr-panel-empty" v-else-if="sortedItems.length === 0">
              <div class="empty-icon"><fa-icon :icon="['fal', 'check-circle']" size="2x"/></div>
              <p class="empty-title">{{ emptyTitle }}</p>
              <p class="empty-subtitle">{{ emptyText }}</p>
            </div>
            <template v-else>
              <guides-table
                :items="sortedItems"
                :selected.sync="selected"
                :printing-id="printingId"
                :deleting-id="deletingId"
                :can-delete="canDelete"
                :sort="sort"
                @open="openGuide"
                @print="printGuide"
                @batch="goToBatch"
                @remove="removeGuide"
                @sort="setSort"
              />
              <div class="load-more" v-if="hasMore">
                <button class="btn btn-primary" :class="{loading: loadingMore}"
                        :disabled="loadingMore" @click="loadMore">Carregar mais</button>
                <small>mostrando {{ data.items.length }} de {{ data.total }}</small>
              </div>
            </template>
          </template>
        </div>
      </div>
      <div class="card-footer gd-footer" v-if="data.items.length > 0">
        <span>Guias no filtro: <strong class="text-primary">{{ data.total }}</strong></span>
        <span class="gd-footer-sort">Ordenado por {{ sortText }}</span>
      </div>
    </div>
    <forbidden v-else></forbidden>

    <guides-selection-bar
      v-if="selected.length > 0"
      :items="selectedItems"
      :limit="batchLimit"
      :plan-name="selectedPlanName"
      :type-name="selectedTypeName"
      :homogeneous="homogeneous"
      :can-batch="canCreateBatch"
      :printing="printingSelection"
      :can-reprice="canUpdate"
      :repricing="repriceModal.loading"
      @clear="selected = []"
      @print="printSelection"
      @batch="addToBatch"
      @reprice="openReprice"
    />

    <guides-reprice-modal
      v-if="repriceModal.show"
      :show="repriceModal.show"
      :preview="repriceModal.preview"
      :applying="repriceModal.applying"
      :result="repriceModal.result"
      @confirm="applyReprice"
      @close="closeReprice"
    />

    <guide-modal
      v-if="guideModal.show"
      :show="guideModal.show"
      :account-id="guideModal.accountId"
      :type="guideModal.type"
      :appointment="guideModal.appointment"
      :insurance="guideModal.insurance"
      :preselected-expense-ids="guideModal.preselectedExpenseIds"
      @saved="guideSaved"
      @deleted="guideDeleted"
      @close="guideModal.show = false"
    />

    <new-guide-picker
      v-if="newGuide.show"
      :show="newGuide.show"
      @pick="pickNewGuide"
      @close="newGuide.show = false"
    />
  </div>
</template>

<script>
import moment from 'moment';
import localforage from 'localforage';
import { mapActions, mapState } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { date, minDate, maxDate } from '@/data/validators';
import {
  LIST_ACCOUNTS, UPDATE_ACCOUNT, DELETE_ACCOUNT, CREATE_BILLING,
} from '@/data/actions/modules/financial';
import { getName as typeName } from '@/data/billing-guide-types';
import Forbidden from '@/components/auth/Forbidden.vue';
import {
  list, remove, loadPlan, printRoute, batchLimitOf, repricePreviewMany, repriceApplyMany,
} from '../../guide/service';
import GuidesSummary from '../../guide/components/GuidesSummary.vue';
import GuidesToolbar from '../../guide/components/GuidesToolbar.vue';
import GuidesTable from '../../guide/components/GuidesTable.vue';
import GuidesSelectionBar from '../../guide/components/GuidesSelectionBar.vue';
import GuideModal from '../../guide/modals/GuideModal.vue';
import NewGuidePicker from '../../guide/modals/NewGuidePicker.vue';
import GuidesRepriceModal from '../../guide/modals/GuidesRepriceModal.vue';

const STORAGE_KEY = 'billing-guides:plan';

const SITUATIONS = {
  expired: 'Fora do prazo de apresentação',
  glossed: 'Com glosa',
};
const LIMIT = 100;

function blankFilter() {
  return {
    insurancePlanId: '',
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD'),
    professionalId: '',
    types: ['consultation', 'sadt', 'simple'],
    link: 'none',
    batch: null,
    statuses: ['open'],
    situation: '',
    search: '',
  };
}

function blankSummary() {
  return {
    open: { count: 0, total: 0 },
    inBatch: { count: 0, total: 0 },
    closed: { count: 0, total: 0 },
  };
}

export default {
  components: {
    Forbidden,
    GuidesSummary,
    GuidesToolbar,
    GuidesTable,
    GuidesSelectionBar,
    GuideModal,
    NewGuidePicker,
    GuidesRepriceModal,
  },
  data() {
    return {
      loading: false,
      loadingMore: false,
      summaryLoading: false,
      error: null,
      insurances: [],
      professionals: [],
      filter: blankFilter(),
      data: {
        items: [],
        total: 0,
        totals: { insurance: 0, particular: 0 },
        offset: 0,
        limit: LIMIT,
      },
      sort: { field: 'startDate', direction: 'desc' },
      summary: blankSummary(),
      summaryActive: 'none',
      selected: [],
      planSettingsCache: {},
      printingId: null,
      printingSelection: false,
      deletingId: null,
      guideModal: {
        show: false,
        accountId: null,
        type: 'sadt',
        appointment: null,
        insurance: null,
        preselectedExpenseIds: [],
      },
      newGuide: { show: false },
      repriceModal: {
        show: false,
        loading: false,
        applying: false,
        preview: null,
        result: null,
      },
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    hasAccess() {
      return this.$can(LIST_ACCOUNTS);
    },
    situationLabel() {
      return SITUATIONS[this.filter.situation] || '';
    },
    canUpdate() {
      return this.$can(UPDATE_ACCOUNT);
    },
    canDelete() {
      return this.$can(DELETE_ACCOUNT);
    },
    canCreateBatch() {
      return this.$can(CREATE_BILLING);
    },
    currentInsurance() {
      return this.insurances.find(item => item.id === this.filter.insurancePlanId) || null;
    },
    planName() {
      return this.currentInsurance ? this.currentInsurance.name : 'Todos os convênios';
    },
    periodText() {
      const start = moment(this.filter.startDate);
      const end = moment(this.filter.endDate);
      if (!start.isValid() || !end.isValid()) return '';
      return `${start.format('DD/MM')} – ${end.format('DD/MM/YYYY')}`;
    },
    viewText() {
      const labels = { none: 'Sem lote', with: 'Em lote', all: 'Todas as guias' };
      return labels[this.filter.link];
    },
    sortText() {
      const names = { startDate: 'data', patient: 'paciente', insurance: 'valor do convênio' };
      const direction = this.sort.direction === 'desc' ? 'decrescente' : 'crescente';
      return `${names[this.sort.field]} (${direction})`;
    },
    hasMore() {
      return this.data.offset + this.data.limit < this.data.total;
    },
    sortedItems() {
      const { field, direction } = this.sort;
      const factor = direction === 'asc' ? 1 : -1;
      const valueOf = (item) => {
        if (field === 'patient') return item.patient.name || '';
        if (field === 'insurance') return Number(item.totals.insurance) || 0;
        return item.startDate || '';
      };
      return [...this.data.items].sort((a, b) => {
        const left = valueOf(a);
        const right = valueOf(b);
        if (left === right) return 0;
        return left > right ? factor : -factor;
      });
    },
    selectedItems() {
      return this.data.items.filter(item => this.selected.includes(item.id));
    },
    homogeneous() {
      const [first] = this.selectedItems;
      if (!first) return true;
      return this.selectedItems.every(item => item.insurance.plan.id === first.insurance.plan.id
        && item.type === first.type);
    },
    selectedPlanName() {
      const [first] = this.selectedItems;
      if (!first) return '';
      const found = this.insurances.find(item => item.id === first.insurance.plan.id);
      return found ? found.name : first.insurance.name;
    },
    selectedTypeName() {
      const [first] = this.selectedItems;
      return first ? typeName(first.type) : '';
    },
    batchLimit() {
      const [first] = this.selectedItems;
      if (!first) return 100;
      return batchLimitOf(this.planSettingsCache[first.insurance.plan.id]);
    },
    emptyTitle() {
      if (this.filter.link === 'none') return 'Nenhuma guia sem lote neste período';
      if (this.filter.link === 'with') return 'Nenhuma guia em lote neste período';
      return 'Nenhuma guia encontrada';
    },
    emptyText() {
      if (this.filter.link === 'none') {
        return 'Tudo faturado. Mude o vínculo para "Com lote" ou o período.';
      }
      return 'Verifique os filtros para realizar novas buscas.';
    },
  },
  validations() {
    const { startDate } = this.filter;
    const validStart = startDate && moment(startDate, 'YYYY-MM-DD', true).isValid();
    return {
      filter: {
        insurancePlanId: { required },
        startDate: { required, date },
        endDate: {
          required,
          date,
          minDate: minDate(validStart ? startDate : null),
          maxDate: maxDate(validStart ? moment(startDate).add(3, 'months').format('YYYY-MM-DD') : null),
        },
      },
    };
  },
  watch: {
    selected() {
      const [first] = this.selectedItems;
      if (first) this.planSettingsOf(first);
    },
  },
  async mounted() {
    if (!this.hasAccess) return;
    document.addEventListener('keydown', this.onKeydown);
    await Promise.all([this.loadInsurances(), this.loadProfessionals()]);
    const stored = await localforage.getItem(STORAGE_KEY).catch(() => null);
    if (stored && (stored === 'ALL' || this.insurances.some(item => item.id === stored))) {
      this.filter.insurancePlanId = stored;
    }
    this.applyQuery();
    if (this.filter.insurancePlanId) {
      this.reload();
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    ...mapActions({
      apiFetchProfessionals: 'apiFetchProfessionals',
    }),
    loadInsurances() {
      return this.$http.get('/insurance-plans', { params: { limit: 0, active: true } })
        .then(({ data }) => {
          this.insurances = data.items
            .filter(item => item.plan && item.plan.type !== 'particular')
            .map(item => ({
              id: item.plan.id,
              insuranceId: item.id,
              type: item.type,
              name: item.customName,
            }));
        })
        .catch(() => {});
    },
    loadProfessionals() {
      return this.apiFetchProfessionals()
        .then((items) => {
          this.professionals = items;
        })
        .catch(() => {});
    },
    baseParams() {
      const params = {
        startDate: this.filter.startDate,
        endDate: this.filter.endDate,
      };
      if (this.currentInsurance) {
        params.insuranceId = this.currentInsurance.insuranceId;
        params.planId = this.currentInsurance.id;
      }
      if (this.filter.professionalId) params.professionalId = this.filter.professionalId;
      if (this.filter.types.length > 0 && this.filter.types.length < 3) {
        params.type = this.filter.types.join(',');
      }
      return params;
    },
    listParams() {
      const params = {
        ...this.baseParams(),
        limit: this.data.limit,
        offset: this.data.offset,
      };
      if (this.filter.link === 'none') {
        params.hasBatch = false;
        params.status = 'open';
      } else if (this.filter.link === 'with') {
        params.hasBatch = true;
        if (this.filter.batch && this.filter.batch.id) params.batchId = this.filter.batch.id;
      }
      if (this.filter.link !== 'none' && this.filter.statuses.length > 0) {
        params.status = this.filter.statuses.join(',');
      }
      if (this.filter.situation) params.situation = this.filter.situation;
      if (this.filter.search) params.search = this.filter.search.trim();
      return params;
    },
    planChanged() {
      localforage.setItem(STORAGE_KEY, this.filter.insurancePlanId).catch(() => {});
      this.clearData();
      if (this.filter.insurancePlanId) this.reload();
    },
    setLink(value) {
      this.filter.link = value;
      this.filter.batch = null;
      if (value === 'none') this.filter.statuses = ['open'];
      else if (value === 'with') this.filter.statuses = ['in_batch', 'closed'];
      else this.filter.statuses = ['open', 'in_batch', 'closed'];
      this.reload();
    },
    setSort(field) {
      if (this.sort.field === field) {
        this.sort.direction = this.sort.direction === 'asc' ? 'desc' : 'asc';
      } else {
        this.sort = { field, direction: field === 'startDate' ? 'desc' : 'asc' };
      }
    },
    applySummaryFilter(kind) {
      if (kind === 'closed') {
        this.filter.link = 'with';
        this.filter.batch = null;
        this.filter.statuses = ['closed'];
        this.reload();
        return;
      }
      this.setLink(kind);
    },
    applyQuery() {
      const query = this.$route.query || {};
      const plan = query.insurancePlanId ? String(query.insurancePlanId) : '';
      if (plan === 'ALL' || this.insurances.some(item => item.id === plan)) {
        this.filter.insurancePlanId = plan;
      }
      if (moment(query.startDate, 'YYYY-MM-DD', true).isValid()) {
        this.filter.startDate = String(query.startDate);
      }
      if (moment(query.endDate, 'YYYY-MM-DD', true).isValid()) {
        this.filter.endDate = String(query.endDate);
      }
      if (query.link === 'all' || query.link === 'with' || query.link === 'none') {
        this.setLinkFilter(String(query.link));
      }
      if (!SITUATIONS[query.situation]) return;
      this.filter.situation = String(query.situation);
      if (this.filter.situation === 'glossed') {
        this.filter.link = 'with';
        this.filter.statuses = ['in_batch', 'closed'];
      } else {
        this.filter.link = 'none';
        this.filter.statuses = ['open'];
      }
    },
    setLinkFilter(link) {
      this.filter.link = link;
      if (link === 'none') this.filter.statuses = ['open'];
      else if (link === 'with') this.filter.statuses = ['in_batch', 'closed'];
      else this.filter.statuses = ['open', 'in_batch', 'closed'];
    },
    clearSituation() {
      this.filter.situation = '';
      this.reload();
    },
    clearFilters() {
      const { insurancePlanId } = this.filter;
      this.filter = { ...blankFilter(), insurancePlanId };
      this.$v.filter.$reset();
      this.reload();
    },
    clearData() {
      this.data = {
        items: [],
        total: 0,
        totals: { insurance: 0, particular: 0 },
        offset: 0,
        limit: LIMIT,
      };
      this.selected = [];
      this.error = null;
    },
    syncSummaryActive() {
      const onlyClosed = this.filter.statuses.length === 1 && this.filter.statuses[0] === 'closed';
      this.summaryActive = this.filter.link === 'with' && onlyClosed ? 'closed' : this.filter.link;
    },
    reload() {
      this.data.offset = 0;
      this.selected = [];
      this.syncSummaryActive();
      this.loadSummary();
      return this.load();
    },
    loadMore() {
      this.data.offset += this.data.limit;
      return this.load();
    },
    async load() {
      this.$v.filter.$touch();
      if (this.$v.filter.$error) return;

      const firstData = this.data.offset === 0;
      if (firstData) this.loading = true; else this.loadingMore = true;
      this.error = null;

      try {
        const result = await list(this.listParams());
        this.data.total = result.total || 0;
        this.data.totals = result.totals || { insurance: 0, particular: 0 };
        this.data.items = firstData ? result.items : [...this.data.items, ...result.items];
      } catch (e) {
        this.error = e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Verifique a conexão e tente novamente.';
        this.$toast.error(e);
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    async loadSummary() {
      this.$v.filter.$touch();
      if (this.$v.filter.$error) return;
      this.summaryLoading = true;
      const base = this.baseParams();
      try {
        const [open, inBatch, closed] = await Promise.all([
          list({
            ...base, status: 'open', hasBatch: false, limit: 1, offset: 0,
          }),
          list({
            ...base, status: 'in_batch', limit: 1, offset: 0,
          }),
          list({
            ...base, status: 'closed', limit: 1, offset: 0,
          }),
        ]);
        this.summary = {
          open: { count: open.total, total: open.totals.insurance || 0 },
          inBatch: { count: inBatch.total, total: inBatch.totals.insurance || 0 },
          closed: { count: closed.total, total: closed.totals.insurance || 0 },
        };
      } catch (e) {
        this.summary = blankSummary();
      } finally {
        this.summaryLoading = false;
      }
    },
    async planSettingsOf(item) {
      const planId = item.insurance.plan.id;
      if (!this.planSettingsCache[planId]) {
        const plan = await loadPlan(item.insurance.id, planId).catch(() => null);
        this.$set(this.planSettingsCache, planId, plan && plan.settings ? plan.settings : {});
      }
      return this.planSettingsCache[planId];
    },
    openGuide(item) {
      this.guideModal = {
        show: true,
        accountId: item.id,
        type: item.type,
        appointment: null,
        insurance: null,
        preselectedExpenseIds: [],
      };
    },
    pickNewGuide({
      appointment, insurance, type, preselectedExpenseIds,
    }) {
      this.newGuide.show = false;
      this.guideModal = {
        show: true,
        accountId: null,
        type,
        appointment,
        insurance,
        preselectedExpenseIds,
      };
    },
    guideSaved(account) {
      const index = this.data.items.findIndex(item => item.id === account.id);
      const { appointments, ...saved } = account;
      if (index >= 0) {
        this.data.items.splice(index, 1, saved);
      } else if (this.filter.insurancePlanId) {
        this.data.items.unshift(saved);
        this.data.total += 1;
      }
      this.loadSummary();
    },
    guideDeleted({ id }) {
      this.data.items = this.data.items.filter(item => item.id !== id);
      this.selected = this.selected.filter(selectedId => selectedId !== id);
      this.data.total = Math.max(this.data.total - 1, 0);
      this.loadSummary();
    },
    printGuide(item) {
      this.printingId = item.id;
      return this.$file.print(printRoute(item.id))
        .catch(() => {})
        .finally(() => {
          this.printingId = null;
        });
    },
    async printSelection() {
      this.printingSelection = true;
      try {
        await this.selectedItems.reduce(
          (chain, item) => chain.then(() => this.$file.print(printRoute(item.id))),
          Promise.resolve(),
        );
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.printingSelection = false;
      }
    },
    fillRepriceRows(preview) {
      preview.accounts.forEach((account) => {
        const found = this.data.items.find(item => item.id === account.id);
        if (!found) return;
        if (!account.number) account.number = found.referral.provider || found.code;
        if (!account.patientName) account.patientName = found.patient.name;
        if (!account.startDate) account.startDate = found.startDate;
        if (!account.type) account.type = found.type;
      });
      return preview;
    },
    async openReprice() {
      if (!this.canUpdate || this.selected.length === 0) return;
      this.repriceModal.loading = true;
      try {
        const preview = this.fillRepriceRows(await repricePreviewMany(this.selected));
        if (preview.changed === 0 && preview.blocked === 0) {
          this.$toast.success('Nada a atualizar: as guias selecionadas já estão com a regra vigente.');
          return;
        }
        this.repriceModal.preview = preview;
        this.repriceModal.result = null;
        this.repriceModal.show = true;
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.repriceModal.loading = false;
      }
    },
    async applyReprice() {
      const ids = this.repriceModal.preview.accounts
        .filter(account => !account.blocked && account.changed > 0)
        .map(account => account.id);
      if (ids.length === 0) return;
      this.repriceModal.applying = true;
      try {
        this.repriceModal.result = await repriceApplyMany(ids);
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.repriceModal.applying = false;
      }
    },
    closeReprice() {
      const applied = !!this.repriceModal.result && this.repriceModal.result.updated > 0;
      this.repriceModal.show = false;
      this.repriceModal.preview = null;
      this.repriceModal.result = null;
      if (applied) this.reload();
    },
    goToBatch(item) {
      if (!item.batch || !item.batch.id) return;
      this.$router.push(`/financial/billing-batches/${item.batch.id}`);
    },
    addToBatch() {
      if (!this.homogeneous || this.selectedItems.length === 0) return;
      const [first] = this.selectedItems;
      this.$router.push({
        path: '/financial/billing-batches',
        query: {
          planId: first.insurance.plan.id,
          insuranceId: first.insurance.id,
          guideType: first.type,
          accountIds: this.selected.join(','),
        },
      });
    },
    removeGuide(item) {
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>Deseja realmente excluir a guia nº ${item.referral.provider || item.code}? `
          + 'Os itens voltam a ficar pendentes no agendamento.</div></div>',
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              close();
              this.confirmRemove(item);
            },
          },
        ],
      });
    },
    async confirmRemove(item) {
      this.deletingId = item.id;
      try {
        await remove(item.id);
        this.$toast.success('Guia excluída.');
        this.guideDeleted({ id: item.id });
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.deletingId = null;
      }
    },
    onKeydown(event) {
      const tag = event.target && event.target.tagName ? event.target.tagName.toLowerCase() : '';
      const typing = ['input', 'textarea', 'select'].includes(tag);
      if (event.key === 'Escape' && this.selected.length > 0 && !this.guideModal.show) {
        this.selected = [];
        return;
      }
      if (typing || this.guideModal.show || this.newGuide.show) return;
      if (event.key === '/') {
        event.preventDefault();
        if (this.$refs.toolbar) this.$refs.toolbar.focusSearch();
        return;
      }
      if (event.key === 'a' && this.selected.length > 0) {
        event.preventDefault();
        this.addToBatch();
      }
    },
  },
};
</script>

<style lang="scss">
@import "../../guide/guide.scss";

.billing-guides-page {
  .cr-panel {
    margin-top: $layout-spacing;
  }
  .cr-panel-loading {
    margin: $layout-spacing-xl 0;
  }
  .gd-guides-table {
    tbody tr {
      cursor: pointer;
    }
    &:focus {
      outline: none;
    }
  }
  .gd-footer {
    align-items: baseline;
    display: flex;
    gap: $layout-spacing;
    justify-content: space-between;
  }
  .gd-footer-sort {
    color: $gray-color-dark;
  }
}
</style>
